import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useQuery } from '@apollo/client';
import { brandConfig } from '../../../brandConfig';
import { GET_SEO_METADATA_HEAD } from '../queries';
import { buildIpfsUrl, useIpfsGatewayUrl } from '../utils/ipfs';

interface SEOHeadProps {
  pagePath: string;
  fallbackTitle?: string;
  fallbackDescription?: string;
}

/**
 * Get parent path from a given path
 * e.g., "/businessbuilder/tokenomics" -> "/businessbuilder"
 * e.g., "/businessbuilder" -> "/"
 * e.g., "/" -> null
 */
const getParentPath = (path: string): string | null => {
  if (path === '/') return null;
  const segments = path.split('/').filter(Boolean);
  if (segments.length <= 1) return '/';
  return '/' + segments.slice(0, -1).join('/');
};

/**
 * SEOHead Component
 *
 * Automatically fetches and injects SEO metadata for a given page path.
 * Uses react-helmet-async to manage <head> tags.
 *
 * Hierarchical fallback for social sharing:
 * 1. Try exact path (e.g., /businessbuilder/tokenomics)
 * 2. Try parent path (e.g., /businessbuilder)
 * 3. Try root path (/)
 *
 * Usage:
 * ```tsx
 * <SEOHead
 *   pagePath="/solar/calculator"
 *   fallbackTitle="Solar Calculator"
 *   fallbackDescription="Calculate your solar ROI"
 * />
 * ```
 */
export const SEOHead: React.FC<SEOHeadProps> = ({
  pagePath,
  fallbackTitle = brandConfig.siteName,
  fallbackDescription = brandConfig.description,
}) => {
  const ipfsGateway = useIpfsGatewayUrl();
  // Derive domain from contact email
  const siteDomain = brandConfig.contact.email?.split('@')[1] || 'example.com';

  // Calculate parent path for hierarchical fallback
  const parentPath = getParentPath(pagePath);
  const hasParent = parentPath !== null && parentPath !== '/';

  // Query for the specific page path
  const { data, loading, error } = useQuery(GET_SEO_METADATA_HEAD, {
    variables: { pagePath },
    fetchPolicy: 'cache-first',
  });

  // Query for the parent path (e.g., /businessbuilder for /businessbuilder/tokenomics)
  const { data: parentData, loading: parentLoading } = useQuery(GET_SEO_METADATA_HEAD, {
    variables: { pagePath: parentPath },
    fetchPolicy: 'cache-first',
    skip: !hasParent, // Skip if no parent or parent is root
  });

  // Query for the root "/" as final fallback
  const { data: rootData, loading: rootLoading } = useQuery(GET_SEO_METADATA_HEAD, {
    variables: { pagePath: '/' },
    fetchPolicy: 'cache-first',
    skip: pagePath === '/', // Don't query if we're already on homepage
  });

  // Hierarchical SEO data: exact -> parent -> root
  const seo = data?.seoMetadata;
  const parentSeo = parentData?.seoMetadata;
  const rootSeo = rootData?.seoMetadata;

  // Combined fallback: try parent first, then root
  const fallbackSeo = parentSeo || rootSeo;

  // Basic SEO - page-specific only (no fallback to default for these)
  const title = seo?.metaTitle || fallbackTitle;
  const description = seo?.metaDescription || fallbackDescription;
  const keywords = seo?.metaKeywords?.join(', ') || '';
  const canonicalUrl = seo?.canonicalUrl || `https://www.${siteDomain}${pagePath}`;
  const robots = seo?.robots || 'index,follow';

  // Open Graph - hierarchical fallback: exact -> parent -> root.
  // Hash-only canonical: render URL from CID + tenant gateway at request time.
  const ogTitle = seo?.ogTitle || fallbackSeo?.ogTitle || title;
  const ogDescription = seo?.ogDescription || fallbackSeo?.ogDescription || description;
  const ogImage = buildIpfsUrl(
    (seo as any)?.ogImageCid || (fallbackSeo as any)?.ogImageCid,
    ipfsGateway
  );
  const ogType = seo?.ogType || fallbackSeo?.ogType || 'website';

  // Twitter Card - hierarchical fallback
  const twitterCard = seo?.twitterCard || fallbackSeo?.twitterCard || 'summary_large_image';
  const twitterTitle = seo?.twitterTitle || fallbackSeo?.twitterTitle || ogTitle;
  const twitterDescription = seo?.twitterDescription || fallbackSeo?.twitterDescription || ogDescription;
  const twitterImage = buildIpfsUrl(
    (seo as any)?.twitterImageCid || (fallbackSeo as any)?.twitterImageCid,
    ipfsGateway
  ) || ogImage;

  // LinkedIn - hierarchical fallback
  const linkedInTitle = seo?.linkedInTitle || fallbackSeo?.linkedInTitle || ogTitle;
  const linkedInImage = buildIpfsUrl(
    (seo as any)?.linkedInImageCid || (fallbackSeo as any)?.linkedInImageCid,
    ipfsGateway
  ) || ogImage;
  const linkedInDescription = seo?.linkedInDescription || fallbackSeo?.linkedInDescription || ogDescription;

  // Pinterest - hierarchical fallback
  const pinterestImage = buildIpfsUrl(
    (seo as any)?.pinterestImageCid || (fallbackSeo as any)?.pinterestImageCid,
    ipfsGateway
  ) || ogImage;
  const pinterestTitle = seo?.pinterestTitle || fallbackSeo?.pinterestTitle || ogTitle;
  const pinterestDescription = seo?.pinterestDescription || fallbackSeo?.pinterestDescription || ogDescription;

  // Structured Data - page-specific only (don't inherit from default)
  const structuredData = seo?.structuredData
    ? JSON.stringify(seo.structuredData)
    : null;

  // Don't render anything while loading to avoid flash of default content
  const isLoading = loading || (hasParent && parentLoading) || (pagePath !== '/' && rootLoading);
  if (isLoading) return null;

  // Log error but still render fallback SEO
  if (error) {
    console.warn(`SEO metadata fetch error for ${pagePath}:`, error.message);
  }

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph (Facebook, WhatsApp, LinkedIn fallback) */}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={brandConfig.siteName} />

      {/* Twitter Card */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={twitterTitle} />
      <meta name="twitter:description" content={twitterDescription} />
      {twitterImage && <meta name="twitter:image" content={twitterImage} />}

      {/* LinkedIn specific overrides (only if explicitly set) */}
      {((seo as any)?.linkedInImageCid || (fallbackSeo as any)?.linkedInImageCid) && (
        <meta property="og:image:alt" content={linkedInDescription} />
      )}

      {/* Pinterest */}
      <meta name="pinterest-rich-pin" content="true" />
      {((seo as any)?.pinterestImageCid || (fallbackSeo as any)?.pinterestImageCid) && (
        <meta name="pinterest:image" content={pinterestImage} />
      )}
      {(seo?.pinterestTitle || fallbackSeo?.pinterestTitle) && (
        <meta name="pinterest:title" content={pinterestTitle} />
      )}
      {(seo?.pinterestDescription || fallbackSeo?.pinterestDescription) && (
        <meta name="pinterest:description" content={pinterestDescription} />
      )}

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">{structuredData}</script>
      )}
    </Helmet>
  );
};

export default SEOHead;
